import { useContext, useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { fadeIn } from 'components/utils/animations'
import { getFirstName, selectLoggedIn } from 'selectors/accountSelectors'
import TextButton from 'LuxKit/Button/TextButton'
import { getActionIcon } from 'supportAssistant/functions/getActionIcon'
import { connect } from 'react-redux'
import { SocketContext } from 'supportAssistant/contexts/SocketContext'
import { generateChangeDatesItem, generatePackageUpgradeItem, generateSelectDateCheckoutItem } from 'lib/checkout/accommodation/cart'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { pushWithRegion } from 'actions/NavigationActions'
import { getCheckoutPath, getBedbankChangeDatesPath } from 'lib/url/pathsUtils'
import { STEP_ADD_EXTRA_GUESTS, STEP_CAPACITY, STEP_CHANGE_DATES, STEP_CHANGE_PACKAGE } from 'constants/booking'
import { initialiseCheckout } from 'actions/CheckoutActions'
import { canCancelOrderItem, canChangeHotelRooms, getBedbankItem, getHotelItem, isChangeDatesAllowed } from 'supportAssistant/utils/orderUtils'
import useNavigateTo from 'hooks/useNavigateTo'
import ModalContext from 'contexts/ModalContext'
import { BOOKING_CONFIRMATION_PAGE_ROUTE } from 'supportAssistant/constants/supportAssistant'
import { addChatMessage, createNewChat } from 'actions/supportAssistant/supportAssistantActions'
import { DMY_DATE_FORMAT } from 'constants/dateFormats'
import type { MappedRequestAgentCallbackResult } from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import qs from 'qs'
import moment from 'moment'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import useOrderCancellationConfigs from 'hooks/Order/useOrderCancellationConfigs'
import useCartBypassRegionFilter from 'checkout/hooks/useCartBypassRegionFilter'
import generateTaxInvoicePdf from 'components/Pages/MyEscapesPage/PurchaseSummary/Common/Utils/generateTaxInvoicePdf'
import { downloadPdf } from 'lib/web/downloadPdf'
import config from 'constants/config'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { trackClientEvent } from 'analytics/analytics'
import { getMyEscapesActionQueryParams } from './actionQueryParams'

const StyledToggleButton = styled(TextButton)`
  animation: ${fadeIn} 1s;
`

interface MappedStateProps {
  customerId?: string
  isLoggedIn?: boolean
  account: App.AuthAccount
  currentChatId: string | undefined
}

interface Props {
  action: any
  payload?: App.ChatMessageMetaPayload
  order?: App.Order
  offer?: App.Offer
  nlsCarouselVariation?: string
}

function Action(props: Props & MappedStateProps) {
  const {
    action,
    currentChatId,
    account,
    customerId,
    isLoggedIn,
    offer,
    order,
    payload,
    nlsCarouselVariation,
  } = props

  // const [queuedLoginMessage, setQueuedLoginMessage] = useState<{message: string, chatMessageMeta?: App.ChatMessageMeta} | undefined>(undefined)

  const intl = useIntl()
  const dispatch = useAppDispatch()
  const navigateTo = useNavigateTo()
  const cartBypassRegionFilter = useCartBypassRegionFilter(offer?.id ?? '')
  const bypassRegionSettingId = useAppSelector(state => state.marketing.bypassRegionSettingId)

  const { onLogin } = useContext(SocketContext)
  const showModal = useContext(ModalContext)

  const cancellationConfigId = useMemo(() => {
    if (!order) return undefined

    if (order?.items.length > 0) {
      return getHotelItem(order, payload?.itemId).id
    } else if (order?.bedbankItems.length > 0) {
      return getBedbankItem(order).rooms[0]?.roomId
    }
  }, [order, payload])

  const [cancellationConfig] = useOrderCancellationConfigs(cancellationConfigId, order?.id)

  const requestAgentCallback = useCallback(async(text: string) => {
    try {
      const results = await showModal<MappedRequestAgentCallbackResult | undefined>(
        <RequestAgentCallbackModal type={payload?.genesysAgentCallbackType ?? 'LESA'} isLESATouchpoint chatId={currentChatId} />,
      )

      if (results) {
        let updatedPayload: App.ChatMessageMetaPayload

        if (results.customerHasRecentlyScheduledCallback) {
          updatedPayload = {
            ...payload,
            customerHasRecentlyScheduledCallback: results.customerHasRecentlyScheduledCallback,
          }
        }
        else if (results.isImmediateCallback) {
          updatedPayload = {
            ...payload,
            isImmediateCallback: results.isImmediateCallback,
          }
        } else {
          updatedPayload = {
            ...payload,
            isImmediateCallback: results.isImmediateCallback,
            callbackStartTime: results.customer.callbackTime,
            callbackEndTime: addHalfHour(results.customer.callbackTime!),
            callbackDate: moment(results.customer.callbackDate).format(DMY_DATE_FORMAT),
          }
        }

        dispatch(addChatMessage(text, { action: 'requestAgentCallback', payload: updatedPayload }))
      } else {
        dispatch(addChatMessage(text, { action: 'requestAgentCallbackCancel' }))
      }
    } catch {
      dispatch(addChatMessage(text, { action: 'requestAgentCallbackError' }))
    }
  }, [showModal, payload, currentChatId, dispatch])

  function addHalfHour(time: string) {
    const [hours, minutes] = time.split(':').map(Number)
    const date = moment().hours(hours).minutes(minutes)

    const newDate = date.add(30, 'minute')

    return newDate.format('HH:mm')
  }

  const showLoginModal = useCallback(async(text: string, action: App.ChatActions) => {
    // Create the chat before we fire the login modal.
    // This ensures a smooth transition to logged in.
    if (currentChatId === 'default') {
      dispatch(createNewChat())
    }

    onLogin().then(({ loggedIn, account }) => {
      if (loggedIn && account) {
        // The login modal's customer information is updated before the customer record in state is.
        // Because of this, we are grabbing the customer details from the socketContext onLogin function.
        // So that the details can be included in the messageMetaData payload.
        // This ensures we get the correct default message on login through the ChatActions
        // Once the user state has been updated, it is safe to inject the customer details into the payload
        // in the existing addChatMessage action

        if (action === 'requestAgentCallback') {
          requestAgentCallback(text)
          return
        }

        const updatedPayload = {
          ...payload,
          customerId: account.memberId,
          firstName: account.givenName,
          lastName: account.surname,
          phone: account.email,
          email: account.email,
        }

        // setQueuedLoginMessage({ message: text, chatMessageMeta: { action, payload: updatedPayload } })
        dispatch(addChatMessage(text, { action, payload: updatedPayload }))
      }

      if (action === 'requestAgentCallback') {
        dispatch(addChatMessage(text, { action: 'loginCancelledWhenRequestingCallback' }))
      }
    })
  }, [currentChatId, dispatch, onLogin, payload, requestAgentCallback])

  const handleChangeDatesActionClick = useCallback(() => {
    if (!order) {
      return
    }

    if (order.items.length > 0) {
      if (offer) {
        const item = getHotelItem(order, payload?.itemId)
        const cartItem = generateChangeDatesItem(offer, order, item, undefined, cartBypassRegionFilter, bypassRegionSettingId)

        dispatch(initialiseCheckout([cartItem], {
          regionCode: order?.regionCode,
          currencyCode: order?.currencyCode,
          mode: 'change-dates',
          order,
        }))

        dispatch(pushWithRegion(getCheckoutPath(STEP_CHANGE_DATES), undefined, { fromSupportChat: true }))
      }
    } else if (order.bedbankItems.length > 0) {
      const item = getBedbankItem(order)
      const changeDatesPath = getBedbankChangeDatesPath(item.id, order.id)
      navigateTo(changeDatesPath)
    }
  }, [dispatch, navigateTo, offer, order, payload, cartBypassRegionFilter, bypassRegionSettingId])

  const handleAddExtraGuestsActionClick = useCallback(() => {
    if (!order) {
      return
    }

    if (order.items.length > 0 && offer) {
      const item = getHotelItem(order, payload?.itemId)

      const cartItem = generateChangeDatesItem(offer, order, item, undefined, cartBypassRegionFilter, bypassRegionSettingId)

      dispatch(initialiseCheckout([cartItem], {
        regionCode: order.regionCode,
        currencyCode: order.currencyCode,
        mode: 'add-extra-guests',
        order,
      }))

      dispatch(pushWithRegion(getCheckoutPath(STEP_ADD_EXTRA_GUESTS), undefined, { fromSupportChat: true }))
    }
  }, [dispatch, offer, order, payload, cartBypassRegionFilter, bypassRegionSettingId])

  const handleAddDatesActionClick = useCallback(() => {
    if (!order) {
      return
    }

    if (order.items.length > 0) {
      if (offer) {
        const item = getHotelItem(order, payload?.itemId)
        const pkg = offer.packages.find(pkg => pkg.uniqueKey === item.package.uniqueKey)

        if (pkg) {
          const cartItem = generateSelectDateCheckoutItem(offer, pkg, order.id, item.id, item.intendedOccupancy)

          dispatch(initialiseCheckout([cartItem], {
            mode: 'select-date',
            regionCode: order.regionCode,
            currencyCode: order.currencyCode,
            order,
          }))

          dispatch(pushWithRegion(getCheckoutPath(STEP_CAPACITY)))
        }
      }
    }
  }, [dispatch, offer, order, payload])

  const handleCancelOrderActionClick = useCallback(() => {
    if (!order || !offer) {
      return
    }

    const item = getHotelItem(order, payload?.itemId)

    if (!canCancelOrderItem(order, item, cancellationConfig) || order.hasFlight) {
      return
    }

    const queryString = qs.stringify({ itemId: item.id, cancellation: true })
    dispatch(pushWithRegion(`${BOOKING_CONFIRMATION_PAGE_ROUTE}/${order.id}`, queryString))
  }, [order, offer, payload?.itemId, cancellationConfig, dispatch])

  const navigateToMyEscapes = useCallback((queryParams: Record<string, unknown>) => {
    if (!order) return
    dispatch(pushWithRegion(`${BOOKING_CONFIRMATION_PAGE_ROUTE}/${order.id}`, qs.stringify(queryParams)))
  }, [order, dispatch])

  const handleDownloadInvoiceClick = useCallback(async() => {
    if (!order) return
    try {
      const blob = await generateTaxInvoicePdf(order.id)
      const fileName = `${config.ORDER_TAX_INVOICE_FILENAME}-${moment(order.createdAt).format('YYYY-MM-DD')}-${order.id}`
      downloadPdf(blob, fileName)
      showSnackbar(intl.formatMessage({
        id: '_1YXF-UBVKIliApXo2Na',
        defaultMessage: 'Invoice downloaded successfully',
        description: 'Success snackbar message after downloading tax invoice',
      }), 'success')
    } catch(error) {
      console.error('Failed to download invoice:', error)
      showSnackbar(intl.formatMessage({
        id: 'c1Dreel3t8LM_f-TRvnj',
        defaultMessage: 'Failed to download invoice. Please try again.',
        description: 'Error snackbar message when tax invoice download fails',
      }), 'critical')
    }
  }, [order, intl])

  const handleChangeRoomClick = useCallback(() => {
    if (!order || !offer) return
    const item = getHotelItem(order, payload?.itemId)
    const cartItem = generatePackageUpgradeItem(offer, order, item, cartBypassRegionFilter, bypassRegionSettingId)
    dispatch(initialiseCheckout([cartItem], {
      order,
      currencyCode: order.currencyCode,
      regionCode: order.regionCode,
      mode: 'change-package',
    }))
    dispatch(pushWithRegion(getCheckoutPath(STEP_CHANGE_PACKAGE)))
  }, [order, offer, payload?.itemId, dispatch, cartBypassRegionFilter, bypassRegionSettingId])

  const handleInsuranceUpsellActionClick = useCallback((order: App.Order) => {
    dispatch(initialiseCheckout([], {
      regionCode: order.regionCode,
      currencyCode: order.currencyCode,
      mode: 'insurance',
      order,
    }))
    dispatch(navigateToCheckout())
  }, [dispatch])

  const handleClick = (actionItem: any) => {
    const chatActionKey: App.ChatActions = actionItem?.action?.key
    let item
    if (order) {
      item = getHotelItem(order, payload?.itemId)
    }

    if (chatActionKey === 'resetPasswordSent' && !isLoggedIn && !payload?.inputPayload) {
      dispatch(addChatMessage(actionItem?.text, { action: 'resetPasswordRequestEmail' }))
      return
    }

    if (chatActionKey === 'requestAgentCallback') {
      requestAgentCallback(actionItem?.text)
      return
    }

    if (chatActionKey === 'nlsViewAllHotels') {
      const eventVariation = nlsCarouselVariation ?? 'not_bucketed'
      const nlsPayload = {
        ...payload,
        ...actionItem?.action?.payload,
        customerId: payload?.customerId ? payload.customerId : customerId,
        firstName: payload?.firstName ? payload.firstName : account.givenName,
      }

      trackClientEvent({
        subject: 'support_assistant',
        action: `lesa_nls_view_all_hotels_clicked_${eventVariation}`,
        category: 'logging',
        type: 'interaction',
        optimizelyEventId: '6294184005468160',
        optimizelyEventKey: 'lesa_nls_view_all_hotels_click',
      })

      if (actionItem?.url) {
        const qIdx = actionItem.url.indexOf('?')
        const path = qIdx >= 0 ? actionItem.url.substring(0, qIdx) : actionItem.url
        const search = qIdx >= 0 ? actionItem.url.substring(qIdx + 1) : undefined
        dispatch(pushWithRegion(path, search))
      }

      dispatch(addChatMessage(actionItem?.text, { action: chatActionKey, payload: nlsPayload }))
      return
    }

    if (order && chatActionKey === 'showFinePrint') {
      dispatch(pushWithRegion(`${BOOKING_CONFIRMATION_PAGE_ROUTE}/${order.id}`, undefined, { fromSupportChat: true, showFinePrint: true }))
      return
    }

    if (order && (chatActionKey === 'manageBooking' || chatActionKey === 'manageBookingByID')) {
      dispatch(pushWithRegion(`${BOOKING_CONFIRMATION_PAGE_ROUTE}/${order.id}`, undefined, { fromSupportChat: true }))
      return
    }

    if (payload?.id_orders && (chatActionKey === 'manageBooking' || chatActionKey === 'manageBookingByID')) {
      dispatch(pushWithRegion(`${BOOKING_CONFIRMATION_PAGE_ROUTE}/${payload.id_orders}`, undefined, { fromSupportChat: true }))
      return
    }

    const canChangeDates = isChangeDatesAllowed(payload, order, offer, cancellationConfig)
    if (order && chatActionKey === 'changeDates') {
      if (canChangeDates && canCancelOrderItem(order, item, cancellationConfig)) {
        handleChangeDatesActionClick()
      }
    }

    if (order && offer && chatActionKey === 'orderAddDates') {
      if (item?.status === 'awaiting_dates') {
        handleAddDatesActionClick()
      }
    }

    if (order && chatActionKey === 'insuranceUpsell') {
      handleInsuranceUpsellActionClick(order)
    }

    if (order && chatActionKey === 'addExtraGuests') {
      handleAddExtraGuestsActionClick()
    }

    let canChangeRooms = false
    if (order && offer) {
      const items = order.items
      if (items?.length > 0) {
        canChangeRooms = canChangeHotelRooms(order, offer, items, item, cancellationConfig)
      }
    }

    if (order && offer && chatActionKey === 'changeRoom' && canChangeRooms) {
      handleChangeRoomClick()
    }

    if (order && !order.hasFlight && offer && chatActionKey === 'cancelOrder') {
      handleCancelOrderActionClick()
    }

    const myEscapesActionQueryParams = getMyEscapesActionQueryParams(chatActionKey, item?.id)
    if (order && myEscapesActionQueryParams) {
      navigateToMyEscapes(myEscapesActionQueryParams)
    }

    if (order && chatActionKey === 'downloadTaxInvoice') {
      handleDownloadInvoiceClick()
    }

    if (!isLoggedIn) {
      switch (chatActionKey) {
        case 'listOrders':
          showLoginModal(actionItem?.text, 'listOrders')
          return
        case 'login':
          showLoginModal(actionItem?.text, 'login')
          return
        case 'loginWithDefaultActions':
          showLoginModal(actionItem?.text, 'loginWithDefaultActions')
          return
        case 'loginWithBookingID':
          showLoginModal(actionItem?.text, 'loginWithBookingID')
          return
        case 'requestAgent':
          showLoginModal(actionItem?.text, 'requestAgent')
          return
      }
    }

    const updatedPayload = {
      ...payload,
      // Include any payload from the action itself (e.g., NLS search query)
      ...actionItem?.action?.payload,
      customerId: payload?.customerId ? payload.customerId : customerId,
      firstName: payload?.firstName ? payload.firstName : account.givenName,
      checkInDate: item?.reservation?.startDate ? item.reservation.startDate : payload?.checkInDate,
    }

    dispatch(addChatMessage(actionItem?.text, { action: chatActionKey, payload: updatedPayload }))
  }

  const ActionIcon = getActionIcon(action?.category)

  return (
    <>
      {action.type === 'button' && <StyledToggleButton
        kind="secondary"
        variant="dark"
        onClick={() => handleClick(action)}
        startIcon={ActionIcon ? <ActionIcon /> : undefined}
      >
        {action?.text}
      </StyledToggleButton>}
      {action.type === 'link' && <StyledToggleButton
        kind="secondary"
        variant="dark"
        to={action?.url}
        target="_blank"
        onClick={() => handleClick(action)}
        startIcon={ActionIcon ? <ActionIcon /> : undefined}
      >
        {action?.text}
      </StyledToggleButton>}
    </>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((state) => {
  return {
    isLoggedIn: selectLoggedIn(state),
    customerId: state.auth.account.memberId,
    firstName: getFirstName(state),
    account: state.auth.account,
    currentChatId: state.supportAssistant.currentChatId,
  }
})(Action)
